/**
 * Comprehend Replay Engine.
 *
 * Implements comprehend-replay-engine-mockup.html against real capture data.
 * The mockup is the source of truth for layout and interaction; this file is the
 * engine behind it.
 *
 * The shape of the thing:
 *
 *   topbar     session identity + sync health + deep link
 *   stage      ONE rectangle containing two rrweb Replayers — the EMR page and
 *              the Comprehend panel — with a draggable seam between them
 *   inspector  All / Network / Messages / Console, sharing one selection
 *   timeline   chapter strip, ruler, four event lanes, one playhead
 *
 * ── Timebase (the part that decides whether any of this is honest) ──────────
 * The design doc calls out that capture contexts do not share a clock and warns
 * against reconstructing sync heuristically at replay time. We are in the good
 * case, and for a specific reason: every stream here is stamped with
 * `Date.now()` — rrweb stamps its own events with it, and the sidecars are
 * emitted as rrweb custom events, so they inherit the same stamping. The EMR
 * recorder runs in a different process but on the same machine, reading the same
 * OS clock. So the two streams are natively on one timebase; there is no offset
 * to estimate.
 *
 * That is why the sync readout says "same clock" instead of quoting a drift
 * figure. The mockup's "drift 11 ms" was sample data, and inventing a number
 * here would be pretending to a measurement we do not make.
 *
 * All UI time is elapsed-from-t0, where t0 is the earlier of the two streams'
 * first event.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { extractSidecars } from '../../api/replay/sidecarStreams';
import { flattenEmrEvents } from './EmrReplayPane';
import { formatReplayTimestamp } from './replayTime';
import { getReplayBounds } from './replayDay';
import './replayEngine.css';

/* ───────────────────────── time helpers ───────────────────────── */

function fmt(ms) {
  if (!Number.isFinite(ms) || ms < 0) ms = 0;
  const total = ms / 1000;
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${String(m).padStart(2, '0')}:${s.toFixed(1).padStart(4, '0')}`;
}

/* ───────────────────────── data shaping ───────────────────────── */

/**
 * Flatten every stream into one chronological list.
 *
 * This is what the All tab reads, and what the lanes are drawn from — one
 * derivation rather than five, so a row and its tick can never disagree about
 * where an event sits.
 */
function buildTimeline(sidecars) {
  const items = [];
  for (const row of sidecars.api) {
    const failed = !!row.error || (row.status && row.status >= 400);
    items.push({
      kind: 'net', lane: 0, ts: row.timestamp, failed, row,
      label: row.url,
      method: (row.method || 'POST').toLowerCase(),
      status: row.error ? 'ERR' : row.status,
    });
  }
  for (const row of sidecars.messages) {
    items.push({
      kind: 'msg', lane: 1, ts: row.timestamp, failed: !!row.error, row,
      label: row.type || 'message',
      dir: row.dir,
    });
  }
  for (const row of sidecars.console) {
    const emr = row.source === 'emr' || row.source === 'injector';
    items.push({
      kind: 'con', lane: emr ? 2 : 3, ts: row.timestamp,
      failed: row.level === 'error', row,
      label: row.message, source: row.source, level: row.level,
    });
  }
  for (const row of sidecars.pages) {
    if (row.kind !== 'page') continue;
    items.push({ kind: 'page', lane: null, ts: row.timestamp, failed: false, row, label: row.url || 'page' });
  }
  items.sort((a, b) => a.ts - b.ts);
  return items;
}

/**
 * Chapter segments from page navigations.
 *
 * Conversations would be the second segmentation the design doc describes, but
 * nothing stamps a conversation id onto the capture yet, so rendering a
 * conversation row would mean drawing something we do not have. One row until
 * that exists.
 */
function buildSegments(sidecars, t0, duration) {
  const pages = sidecars.pages.filter((p) => p.kind === 'page' && p.url);
  if (!pages.length) return [];
  return pages.map((page, i) => {
    const start = page.timestamp - t0;
    const end = i + 1 < pages.length ? pages[i + 1].timestamp - t0 : duration;
    let label = page.url;
    try {
      label = new URL(page.url).pathname || page.url;
    } catch (e) {
      /* keep the raw value */
    }
    return { start, end, label, noteType: page.noteType };
  });
}

/**
 * Collapse ticks that would land within ~2px of each other into a count blob.
 * A busy lane is otherwise a solid bar, which reads as "no information".
 */
function clusterEvents(items, duration, laneWidthPx) {
  if (!laneWidthPx || !duration) return items.map((it) => ({ ...it, count: 1 }));
  const minGap = (2 / laneWidthPx) * duration;
  const out = [];
  for (const it of items) {
    const prev = out[out.length - 1];
    // Errors never merge: the point of an error tick is to be findable.
    if (prev && !it.failed && !prev.failed && it.ts - prev.ts < minGap) {
      prev.count += 1;
    } else {
      out.push({ ...it, count: 1 });
    }
  }
  return out;
}

/**
 * Index of the last item at or before `ts`, or -1. `items` is sorted by ts.
 *
 * Worth a binary search rather than a scan because the callers run per frame
 * and the lists are long: the sampled replays carry 30-45k sidecar rows.
 */
function lastIndexAtOrBefore(items, ts) {
  let low = 0;
  let high = items.length - 1;
  let found = -1;
  while (low <= high) {
    const mid = (low + high) >> 1;
    if (items[mid].ts <= ts) {
      found = mid;
      low = mid + 1;
    } else {
      high = mid - 1;
    }
  }
  return found;
}

// rrweb mouse/touch movement, interaction, scroll, resize, input, drag and
// selection. Background DOM changes and sidecar logging do not imply activity.
const INTERACTION_SOURCES = new Set([1, 2, 3, 4, 5, 6, 12, 14]);

function buildIdleRanges(panelEvents, emrEvents, pages, t0, duration) {
  const activity = new Set([t0, t0 + duration]);
  for (const stream of [panelEvents, emrEvents]) {
    for (const event of stream) {
      if (event.type === 3 && INTERACTION_SOURCES.has(event.data?.source) && Number.isFinite(event.timestamp)) activity.add(event.timestamp);
    }
  }
  for (const page of pages) {
    if (page.kind === 'page' && Number.isFinite(page.timestamp)) activity.add(page.timestamp);
  }
  const times = [...activity].sort((a, b) => a - b);
  const ranges = [];
  for (let i = 1; i < times.length; i += 1) {
    if (times[i] - times[i - 1] > 10000) ranges.push({ ts: times[i - 1] - t0 + 2000, end: times[i] - t0 - 2000 });
  }
  return ranges;
}

/**
 * Position of `selected` within `items`, or -1.
 *
 * Binary search to the timestamp, then walk the rows sharing it looking for the
 * row itself. Sidecar rows are stamped with Date.now(), so a burst of console
 * lines lands on one millisecond and the first index at that timestamp is
 * usually not the selected one.
 */
function indexOfSelected(items, selected) {
  let index = lastIndexAtOrBefore(items, selected.ts);
  if (index === -1) return -1;
  while (index >= 0 && items[index].ts === selected.ts) {
    if (items[index] === selected) return index;
    index -= 1;
  }
  // Not present (it belongs to another tab); the nearest row is good enough to
  // scroll to, which is all the caller wants.
  return lastIndexAtOrBefore(items, selected.ts);
}

/* ───────────────────────── the engine ───────────────────────── */

const SPEEDS = [0.5, 1, 2, 4, 8];

/**
 * Height of one inspector row, in px. MUST match `.inspector-row` in
 * replayEngine.css, which fixes it so the window arithmetic below holds.
 */
const ROW_HEIGHT = 28;
/** Rows drawn above and below the viewport, so a flick does not show a gap. */
const ROW_OVERSCAN = 12;
/**
 * Smallest gap between two player seeks, in ms. Applies to every seek, not just
 * the scrub bar — see requestSeek. A frame was far too often: rrweb rebuilds
 * the recorded DOM on each one, and rebuilding 60 times a second buries the
 * garbage collector.
 */
const SEEK_INTERVAL_MS = 300;
/**
 * How far one arrow key moves the playhead. Large enough that holding the key
 * covers ground, small enough to land on a specific moment.
 */
const SCRUB_STEP_MS = 5000;
const TABS = [
  { key: 'all', label: 'All' },
  { key: 'net', label: 'Network' },
  { key: 'msg', label: 'Messages' },
  { key: 'con', label: 'Console' },
];

const ReplayEngine = ({ replay, events, onClose, entry, onPosition, onEnded, skipInactive = true }) => {
  // Already loadable: extension asset URLs are rewritten per chunk as the
  // replay is fetched (replayLoader), because doing it here
  // meant re-serialising the entire replay into one string.
  const playableEvents = events;
  const sidecars = useMemo(() => extractSidecars(playableEvents), [playableEvents]);
  const emrEvents = useMemo(() => flattenEmrEvents(sidecars.emr), [sidecars.emr]);
  const panelEvents = playableEvents;
  const isWebReplay = replay?.surface === 'web';

  // t0 is the earlier of the two streams so neither is ever seeked negative.
  const { t0, duration } = useMemo(() => {
    const bounds = getReplayBounds(playableEvents);
    const start = bounds?.start ?? 0;
    const end = bounds?.end ?? start + 1;
    return { t0: start, duration: Math.max(1, end - start) };
  }, [playableEvents]);

  const timeline = useMemo(() => buildTimeline(sidecars), [sidecars]);
  const segments = useMemo(() => buildSegments(sidecars, t0, duration), [sidecars, t0, duration]);
  const idleRanges = useMemo(() => buildIdleRanges(panelEvents, emrEvents, sidecars.pages, t0, duration), [panelEvents, emrEvents, sidecars.pages, t0, duration]);

  const [elapsed, setElapsed] = useState(0);
  const elapsedRef = useRef(0);
  const pendingElapsedRef = useRef(null);
  // The latest position ASKED for, which is not the same as the last one React
  // committed: seek() batches setElapsed onto an animation frame, so between a
  // seek and that frame the state is one step behind. Everything that acts on
  // "where is the playhead" — play, arrow steps, the transport clock — reads
  // this, or it acts on a position the user already moved away from.
  elapsedRef.current = pendingElapsedRef.current ?? elapsed;
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(entry?.speed || 1);
  const [playersReady, setPlayersReady] = useState(false);
  const endedRef = useRef(false);
  const [tab, setTab] = useState('all');
  const [selected, setSelected] = useState(null);
  const [follow, setFollow] = useState(true);

  const emrHostRef = useRef(null);
  const cxHostRef = useRef(null);
  const emrRef = useRef(null);
  const cxRef = useRef(null);
  const trackRef = useRef(null);
  const seekTimerRef = useRef(null);
  const elapsedFrameRef = useRef(null);
  const pendingSeekRef = useRef(null);
  const lastSeekAtRef = useRef(0);
  const lastCommittedRef = useRef(-1);
  const rafRef = useRef(null);
  const rowsRef = useRef(null);
  const rootRef = useRef(null);
  const togglePlayRef = useRef(null);
  const nudgeRef = useRef(null);

  const onPlaybackKeyDown = useCallback((event) => {
    // Arrows scrub, Space plays. Held arrows repeat on purpose — that is how a
    // keyboard scrub feels continuous — so `repeat` only disqualifies Space.
    const scrub = event.code === 'ArrowLeft' ? -1 : event.code === 'ArrowRight' ? 1 : 0;
    if (!scrub && event.code !== 'Space') return;
    if ((event.repeat && !scrub) || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey || event.defaultPrevented) return;
    if (!(scrub ? nudgeRef.current : togglePlayRef.current)) return;
    const target = event.target;
    // Only controls that genuinely own these keys keep them: text entry, and
    // widgets where Space or the arrows already mean something.
    //
    // A focused BUTTON is deliberately not in that list. Letting the browser
    // activate it looked harmless — Space on the focused Play button toggled
    // playback, just via activation rather than through here — but it means
    // whatever else happens to hold focus swallows Space instead. Focus lands
    // on a nav control once and from then on Space toggles that, not playback,
    // with no way to tell from the replay that anything is listening. A viewer
    // owns its transport keys; buttons are still reachable with Enter.
    if (target?.isContentEditable || target?.closest?.('input, textarea, select, [role="textbox"], [role="combobox"], [role="listbox"], [role="slider"]')) return;
    if (rootRef.current?.closest('.replay-day')?.querySelector('[role="dialog"]')) return;
    event.preventDefault();
    if (scrub) nudgeRef.current(scrub * SCRUB_STEP_MS);
    else togglePlayRef.current?.();
  }, []);

  useEffect(() => {
    document.addEventListener('keydown', onPlaybackKeyDown);
    return () => document.removeEventListener('keydown', onPlaybackKeyDown);
  }, [onPlaybackKeyDown]);

  /* ── mount both players ── */
  useEffect(() => {
    let cancelled = false;
    let fitTimer;
    const removeFrameKeys = [];
    (async () => {
      const { Replayer } = await import('rrweb');
      await import('rrweb/dist/style.css');
      if (cancelled) return;

      const mount = (host, evts) => {
        if (!host || !evts || evts.length < 2) return null;
        host.innerHTML = '';
        const r = new Replayer(evts, { root: host, mouseTail: false, skipInactive: false });
        r.pause(0);
        let keyboardWindow;
        const bindKeyboard = () => {
          keyboardWindow?.removeEventListener('keydown', onPlaybackKeyDown);
          keyboardWindow = r.iframe.contentWindow;
          keyboardWindow?.addEventListener('keydown', onPlaybackKeyDown);
        };
        bindKeyboard();
        r.on('fullsnapshot-rebuilded', bindKeyboard);
        removeFrameKeys.push(() => keyboardWindow?.removeEventListener('keydown', onPlaybackKeyDown));
        // The iframe has no dimensions until a full snapshot has been rebuilt,
        // so fitting right here would measure zero and quietly do nothing.
        r.on('fullsnapshot-rebuilded', () => fitAll());
        r.on('resize', () => fitAll());
        return r;
      };
      cxRef.current = mount(cxHostRef.current, panelEvents);
      emrRef.current = mount(emrHostRef.current, emrEvents);
      // Belt and braces: the snapshot handler above covers the normal path, and
      // this catches a stream whose first snapshot landed before we subscribed.
      fitTimer = setTimeout(fitAll, 0);
      setPlayersReady(true);
    })();

    return () => {
      cancelled = true;
      clearTimeout(fitTimer);
      removeFrameKeys.forEach(remove => remove());
      for (const ref of [emrRef, cxRef]) {
        try {
          ref.current?.destroy();
        } catch (e) {
          /* already gone */
        }
        ref.current = null;
      }
    };
  }, [panelEvents, emrEvents, onPlaybackKeyDown]);

  /** Scale each player into its pane. Seam drags rescale; they never reflow. */
  const fitAll = useCallback(() => {
    for (const [ref, hostRef] of [[emrRef, emrHostRef], [cxRef, cxHostRef]]) {
      const r = ref.current;
      const host = hostRef.current;
      if (!r || !host) continue;
      try {
        const w = Number(r.iframe.width) || r.iframe.offsetWidth;
        const h = Number(r.iframe.height) || r.iframe.offsetHeight;
        if (!w || !h) continue;
        const scale = Math.min(host.clientWidth / w, host.clientHeight / h);
        r.wrapper.style.transform = `scale(${scale})`;
        r.wrapper.style.transformOrigin = 'top left';
        host.scrollTop = 0;
        host.scrollLeft = 0;
      } catch (e) {
        /* cosmetic */
      }
    }
  }, []);

  useEffect(() => {
    window.addEventListener('resize', fitAll);
    return () => window.removeEventListener('resize', fitAll);
  }, [fitAll]);

  /* ── one seek drives both ── */

  /**
   * Move the players. This is the expensive half and there is no making it
   * cheap: any seek backwards, and any seek that crosses a checkout, makes
   * rrweb rebuild the recorded page from a full snapshot. That rebuild
   * recreates every iframe the page had — seventeen of them on the web app, so
   * seventeen fresh Documents — and the ones it replaces stay on the heap until
   * the collector gets to them. Rebuilding faster than Chrome collects is what
   * takes the tab into the gigabytes, so the thing to control is not the cost
   * of a rebuild but how many of them we ask for. See requestSeek below.
   */
  const commitSeek = useCallback(
    (ms, keepPlaying) => {
      const absolute = t0 + ms;
      lastCommittedRef.current = keepPlaying ? -1 : ms;
      for (const [ref, evts] of [[emrRef, emrEvents], [cxRef, panelEvents]]) {
        const r = ref.current;
        if (!r || !evts.length) continue;
        const rel = absolute - evts[0].timestamp;
        try {
          r.setConfig({ speed });
          if (keepPlaying) r.play(Math.max(0, rel));
          else r.pause(Math.max(0, rel));
        } catch (e) {
          /* seeking past an end is not fatal */
        }
      }
    },
    [t0, emrEvents, panelEvents, speed],
  );
  const commitSeekRef = useRef(commitSeek);
  commitSeekRef.current = commitSeek;

  useEffect(() => {
    if (!playersReady || !entry) return;
    const offset = Math.max(0, Math.min(duration, (entry.timestamp ?? t0) - t0));
    endedRef.current = false;
    setElapsed(offset);
    commitSeekRef.current(offset, entry.playing);
    setPlaying(entry.playing);
  }, [playersReady, entry, t0, duration]);

  useEffect(() => {
    if (playersReady) onPosition?.({ timestamp: t0 + elapsed, playing, speed });
    if (endedRef.current && !playing && elapsed >= duration) {
      endedRef.current = false;
      onEnded?.({ timestamp: t0 + duration, playing: true, speed });
    }
  }, [playersReady, elapsed, playing, speed, t0, duration, onPosition, onEnded]);

  /**
   * Move the playhead now; move the players at most once per SEEK_INTERVAL_MS.
   *
   * Every seek in the engine goes through here — the scrub bar, the lane ticks,
   * the inspector rows, the chapter strip, step and jump-to-error. Only the
   * drag used to be throttled, so a user clicking around the timeline was
   * asking for one full rebuild per click as fast as they could click.
   *
   * `elapsed` is set immediately regardless, so the playhead, the clock and the
   * selection stay glued to the pointer. Only the pictures arrive late, and
   * only ever by SEEK_INTERVAL_MS.
   */
  const seek = useCallback(
    (ms, keepPlaying = false) => {
      const clamped = Math.max(0, Math.min(duration, ms));
      // One React render per frame, not one per pointer move. A drag delivers
      // well over a hundred moves a second and each setElapsed re-renders the
      // whole engine — the lanes, the ruler, the playhead, the inspector. That
      // reconciliation, not the seeking, is most of what is left on the CPU
      // once the seeks themselves are throttled.
      pendingElapsedRef.current = clamped;
      elapsedRef.current = clamped;
      if (elapsedFrameRef.current === null) {
        elapsedFrameRef.current = requestAnimationFrame(() => {
          elapsedFrameRef.current = null;
          const next = pendingElapsedRef.current;
          pendingElapsedRef.current = null;
          if (next !== null) setElapsed(next);
        });
      }
      pendingSeekRef.current = { ms: clamped, keepPlaying };

      const run = () => {
        seekTimerRef.current = null;
        const next = pendingSeekRef.current;
        pendingSeekRef.current = null;
        if (!next) return;
        // Clicking the same row twice should not rebuild the page twice.
        if (!next.keepPlaying && next.ms === lastCommittedRef.current) return;
        lastSeekAtRef.current = performance.now();
        commitSeek(next.ms, next.keepPlaying);
      };

      if (seekTimerRef.current !== null) {
        clearTimeout(seekTimerRef.current);
        seekTimerRef.current = null;
      }
      // Leading edge when the player has been idle, so a single click is
      // instant; trailing edge only while seeks are arriving in a stream.
      const since = performance.now() - lastSeekAtRef.current;
      if (keepPlaying || since >= SEEK_INTERVAL_MS) run();
      else seekTimerRef.current = setTimeout(run, SEEK_INTERVAL_MS - since);
    },
    [duration, commitSeek],
  );

  /* Nothing should outlive the component holding a pending rebuild. */
  useEffect(
    () => () => {
      if (seekTimerRef.current !== null) clearTimeout(seekTimerRef.current);
      if (elapsedFrameRef.current !== null) cancelAnimationFrame(elapsedFrameRef.current);
    },
    [],
  );

  /* ── transport ── */
  useEffect(() => {
    if (!playing) {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      return undefined;
    }
    // The master clock is ours, not either player's: reading one player's
    // getCurrentTime() would make it the leader and let the other drift.
    let last = performance.now();
    // The range the playhead is sitting in when play starts is not skipped: the
    // user put it there, and moving them somewhere else the moment they press
    // play reads as the seek having been ignored. Every LATER idle range still
    // skips, which is what the feature is for.
    const parkedIn = idleRanges[lastIndexAtOrBefore(idleRanges, elapsedRef.current)];
    const parked = parkedIn && elapsedRef.current < parkedIn.end ? parkedIn : null;
    const tick = (now) => {
      const delta = (now - last) * speed;
      last = now;
      let next = elapsedRef.current + delta;
      if (skipInactive) {
        const idle = idleRanges[lastIndexAtOrBefore(idleRanges, next)];
        if (idle && next < idle.end && idle !== parked) {
          next = idle.end;
          // One master clock skips both captures together; rrweb's independent
          // skipInactive clocks would allow the two panes to drift apart.
          commitSeekRef.current(next, true);
        }
      }
      elapsedRef.current = Math.min(next, duration);
      setElapsed(elapsedRef.current);
      if (next >= duration) {
        endedRef.current = true;
        setPlaying(false);
        return;
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [playing, speed, duration, skipInactive, idleRanges]);

  const togglePlay = () => {
    const from = elapsedRef.current;
    const nextElapsed = from >= duration ? 0 : from;
    seek(nextElapsed, !playing);
    setPlaying(!playing);
  };
  togglePlayRef.current = playersReady ? togglePlay : null;

  /**
   * Step the playhead, pausing — the same thing dragging the scrub bar does.
   *
   * Deliberately NOT seek(..., playing) the way the transport buttons do:
   * `keepPlaying` skips both the SEEK_INTERVAL_MS throttle and the repeated-
   * position check, which is harmless for a click and ruinous for a held key.
   * Every repeat would commit a full rrweb rebuild of both players — the exact
   * unbounded rebuild rate the throttle exists to prevent.
   *
   * Steps from elapsedRef, so two key repeats inside one animation frame do
   * not both start from the same stale base and lose one.
   */
  const nudge = (deltaMs) => {
    setPlaying(false);
    seek(elapsedRef.current + deltaMs);
  };
  nudgeRef.current = playersReady ? nudge : null;

  const changeSpeed = (nextSpeed) => {
    setSpeed(nextSpeed);
    for (const ref of [emrRef, cxRef]) {
      try {
        ref.current?.setConfig({ speed: nextSpeed });
      } catch (e) {
        /* player is still mounting */
      }
    }
  };

  /** Next/previous sidecar event. */
  const step = (direction) => {
    const here = t0 + elapsed;
    const candidates = direction > 0
      ? timeline.filter((i) => i.ts > here + 1)
      : timeline.filter((i) => i.ts < here - 1).reverse();
    if (candidates.length) seek(candidates[0].ts - t0, playing);
  };

  const jumpToError = () => {
    const here = t0 + elapsed;
    const next = timeline.find((i) => i.failed && i.ts > here + 1) || timeline.find((i) => i.failed);
    if (next) {
      seek(next.ts - t0, playing);
      setSelected(next);
    }
  };

  /* Keep the latest event selected and visible as playback advances. */
  useEffect(() => {
    if (!follow || !playing) return;
    // `elapsed` changes every animation frame, so this runs ~60 times a second
    // for the whole of playback. Walking the timeline here meant 45,000
    // comparisons per frame on an ordinary replay.
    const index = lastIndexAtOrBefore(timeline, t0 + elapsed);
    const nearest = index === -1 ? null : timeline[index];
    if (nearest && nearest !== selected) setSelected(nearest);
  }, [elapsed, follow, playing, timeline, t0, selected]);

  /* ── scrubbing ── */
  useEffect(() => {
    const el = trackRef.current;
    if (!el) return undefined;
    let dragging = false;

    const msAt = (clientX) => {
      const rect = el.getBoundingClientRect();
      const pct = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
      return pct * duration;
    };

    // The coalescing lives in seek() now, shared with every other way of moving
    // the playhead. A drag is just the noisiest of them.
    const down = (e) => {
      dragging = true;
      setPlaying(false);
      seek(msAt(e.clientX));
    };
    const move = (e) => dragging && seek(msAt(e.clientX));
    const up = (e) => {
      if (!dragging) return;
      dragging = false;
      // Not forced: the trailing edge of the throttle always fires, so the
      // release position lands within SEEK_INTERVAL_MS. Forcing it here would
      // exempt every click from the throttle, since a click is a press and a
      // release — which is exactly the "jumping around a lot" case.
      seek(msAt(e.clientX));
    };
    el.addEventListener('pointerdown', down);
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    return () => {
      el.removeEventListener('pointerdown', down);
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
  }, [duration, seek]);

  /* ── resizers ── */
  const makeDrag = useCallback(
    (compute, reset) => (e) => {
      e.preventDefault();
      const el = rootRef.current;
      const move = (ev) => {
        compute(ev.clientX, el);
        fitAll();
      };
      const up = () => {
        window.removeEventListener('pointermove', move);
        window.removeEventListener('pointerup', up);
        document.body.style.cursor = '';
      };
      document.body.style.cursor = 'col-resize';
      window.addEventListener('pointermove', move);
      window.addEventListener('pointerup', up);
      if (e.detail === 2) {
        reset(el);
        fitAll();
      }
    },
    [fitAll],
  );

  /* ── inspector rows ── */
  const visibleRows = useMemo(() => {
    if (tab === 'all') return timeline.filter((i) => i.kind !== 'page');
    return timeline.filter((i) => i.kind === tab);
  }, [tab, timeline]);
  const playbackRowIndex = lastIndexAtOrBefore(visibleRows, t0 + elapsed);
  const inspectorCursorTop = Math.max(0, Math.min((playbackRowIndex + 1) * ROW_HEIGHT, visibleRows.length * ROW_HEIGHT - 1));

  /**
   * Draw only the rows the inspector can actually show.
   *
   * Every row was being rendered: 45,000 of them on an ordinary replay, six
   * DOM nodes each. That is most of a second of layout before the engine
   * appears, and it makes everything afterwards — scrolling, selecting,
   * playing — fight a quarter of a million nodes. A spacer above and below
   * stands in for the rows that are not drawn, so the scrollbar stays honest.
   *
   * The arithmetic needs every row to be exactly ROW_HEIGHT tall, which is why
   * replayEngine.css now pins `.inspector-row` to a fixed height and line-height
   * rather than letting content size it. Console lines carry emoji and CJK
   * (Synapse logs ✅/🎙️ on ordinary device changes, and consoleTap forwards
   * them), and those glyphs have taller font metrics — left to itself a row is
   * 27, 28, 30 or 31px depending on what is in it. Measuring instead of fixing
   * it does not work: the measured row is whichever row the window starts at,
   * and the window is computed FROM the height, so two heights that imply each
   * other alternate forever and React gives up with "Maximum update depth
   * exceeded" mid-scroll.
   */
  const [rowsViewport, setRowsViewport] = useState({ top: 0, height: 0 });

  useEffect(() => {
    const el = rowsRef.current;
    if (!el) return undefined;
    let frame = null;
    const sync = () => {
      frame = null;
      setRowsViewport((prev) =>
        prev.top === el.scrollTop && prev.height === el.clientHeight
          ? prev
          : { top: el.scrollTop, height: el.clientHeight },
      );
    };
    const schedule = () => {
      if (frame === null) frame = requestAnimationFrame(sync);
    };
    sync();
    el.addEventListener('scroll', schedule, { passive: true });
    // The inspector pane is draggable and the window resizes; without this the
    // window keeps the height it had when the replay opened.
    const observer = new ResizeObserver(schedule);
    observer.observe(el);
    return () => {
      if (frame !== null) cancelAnimationFrame(frame);
      el.removeEventListener('scroll', schedule);
      observer.disconnect();
    };
  }, []);

  const rowWindow = useMemo(() => {
    const span = Math.ceil((rowsViewport.height || 600) / ROW_HEIGHT) + ROW_OVERSCAN * 2;
    // Clamp against the end of the list, not just against zero. Switching from
    // All (45,000 rows) to Messages (a few hundred) while scrolled down leaves
    // a scrollTop far past the new end, and an unclamped start slices past it —
    // rendering no rows at all, under a spacer tall enough that the pane cannot
    // scroll back into view for several seconds.
    const maxStart = Math.max(0, visibleRows.length - span);
    const start = Math.min(maxStart, Math.max(0, Math.floor(rowsViewport.top / ROW_HEIGHT) - ROW_OVERSCAN));
    return { start, end: Math.min(visibleRows.length, start + span) };
  }, [rowsViewport, visibleRows.length]);

  /* Follow the playback cursor, or the pinned selection when following is off.
     Arithmetic also works for rows outside the rendered window. */
  useEffect(() => {
    const el = rowsRef.current;
    if (!el) return;
    const index = follow ? playbackRowIndex + 1 : selected ? indexOfSelected(visibleRows, selected) : -1;
    if (index === -1) return;
    const top = Math.min(index * ROW_HEIGHT, Math.max(0, visibleRows.length * ROW_HEIGHT - ROW_HEIGHT));
    if (top < el.scrollTop) el.scrollTop = top;
    else if (top + ROW_HEIGHT > el.scrollTop + el.clientHeight) {
      el.scrollTop = top + ROW_HEIGHT - el.clientHeight;
    }
  }, [selected, tab, visibleRows, follow, playbackRowIndex]);

  const changeTab = (nextTab) => {
    const nextRows = nextTab === 'all'
      ? timeline.filter((item) => item.kind !== 'page')
      : timeline.filter((item) => item.kind === nextTab);
    setTab(nextTab);
    // A scroll position from a 45,000-row tab means nothing in a 200-row one.
    // With a selection the effect above re-anchors; without one, start at the top.
    if (rowsRef.current) rowsRef.current.scrollTop = 0;
    if (!selected) return;
    if (!nextRows.length) {
      setSelected(null);
      return;
    }
    setSelected(
      nextRows.reduce((nearest, item) =>
        Math.abs(item.ts - selected.ts) < Math.abs(nearest.ts - selected.ts) ? item : nearest,
      ),
    );
  };

  const counts = {
    all: timeline.filter((i) => i.kind !== 'page').length,
    net: sidecars.api.length,
    msg: sidecars.messages.length,
    con: sidecars.console.length,
  };

  const laneItems = useMemo(() => {
    const width = trackRef.current?.clientWidth || 800;
    return [0, 1, 2, 3].map((lane) =>
      clusterEvents(timeline.filter((i) => i.lane === lane), duration, width),
    );
  }, [timeline, duration]);

  const pct = (ts) => ((ts - t0) / duration) * 100;

  return (
    <div className="cpt-replay" ref={rootRef}>
      {/* ── topbar ── */}
      <header className="topbar">
        <div className="brand">
          <span className="dot" />
          Comprehend&nbsp;<small>Replay</small>
        </div>
        <div className="session-meta">
          <span>
            sess <b>{String(replay?.replayId || '').slice(0, 8) || '—'}</b>
          </span>
          <span className="sep">·</span>
          <span>
            <b>{isWebReplay ? 'WEB APP' : sidecars.pages[0]?.emr || replay?.emr || 'unknown EMR'}</b>
            {!isWebReplay && sidecars.pages.find((p) => p.noteType) ? ` · ${sidecars.pages.find((p) => p.noteType).noteType}` : ''}
          </span>
          <span className="sep">·</span>
          <span>{replay?.userEmail || '—'}</span>
          <span className="sep">·</span>
          <span>{formatReplayTimestamp(replay?.startedAt)}</span>
        </div>
        <div className={`pill${emrEvents.length || isWebReplay ? '' : ' warn'}`}>
          <span className="sync-dot" />
          {isWebReplay
            ? '1 capture · web app'
            : emrEvents.length
              ? '2 captures · same clock'
              : '1 capture · no EMR stream'}
        </div>
        <div className="spacer" />
        {onClose ? (
          <button className="btn-ghost" onClick={onClose}>
            Close
          </button>
        ) : null}
      </header>

      {/* ── main ── */}
      <div className="main">
        <section className="stage">
          <div className="stage-head">
            {isWebReplay ? (
              <span className="cap-badge cx">
                <span className="sq" />
                CAPTURE A · WEB APP ({panelEvents.length})
              </span>
            ) : (
              <>
                <span className="cap-badge emr">
                  <span className="sq" />
                  CAPTURE A · EMR PAGE ({emrEvents.length})
                </span>
                <span className="cap-badge cx">
                  <span className="sq" />
                  CAPTURE B · SIDE PANEL ({panelEvents.length})
                </span>
              </>
            )}
          </div>

          <div className={`viewport${isWebReplay ? ' web-capture' : ''}`}>
            <div
              className="seam-handle"
              title="Drag to resize · double-click to reset"
              onPointerDown={makeDrag(
                (x, el) => {
                  const rect = el.querySelector('.viewport').getBoundingClientRect();
                  const w = Math.min(420, Math.max(200, rect.right - x));
                  el.style.setProperty('--cx-w', `${w}px`);
                },
                (el) => el.style.setProperty('--cx-w', '300px'),
              )}
            />
            <div className="pane" ref={emrHostRef}>
              {!emrEvents.length ? (
                <div className="pane-empty">
                  No EMR capture in this session — the tab was never on a page the
                  content script runs in, or the recorder disabled itself.
                </div>
              ) : null}
            </div>
            <div className={`pane cx-pane${isWebReplay ? ' web-pane' : ''}`} ref={cxHostRef} />
          </div>
        </section>

        <div
          className="resizer-v"
          title="Drag to resize · double-click to reset"
          onPointerDown={makeDrag(
            (x, el) => {
              const w = Math.min(560, Math.max(300, window.innerWidth - x));
              el.style.setProperty('--inspector-w', `${w}px`);
            },
            (el) => el.style.setProperty('--inspector-w', '502px'),
          )}
        />

        {/* ── inspector ── */}
        <aside className="inspector">
          <div className="tabs">
            {TABS.map((t) => (
              <button
                key={t.key}
                className={`tab-btn${tab === t.key ? ' on' : ''}`}
                onClick={() => changeTab(t.key)}
              >
                {t.label} <span className="count">{counts[t.key]}</span>
              </button>
            ))}
          </div>

          <div className="ring-meta">
            RING BUFFER
            <div className="ring-bar">
              <i style={{ width: `${Math.min(100, (sidecars.api.length / 100) * 100)}%` }} />
            </div>
            {sidecars.api.length} / 100 API calls
          </div>

          <div className="rows" ref={rowsRef}>
            <div className="rows-content">
              {rowWindow.start > 0 ? <div style={{ height: rowWindow.start * ROW_HEIGHT }} /> : null}
              {visibleRows.slice(rowWindow.start, rowWindow.end).map((item, i) => (
                <InspectorRow
                  key={rowWindow.start + i}
                  item={item}
                  t0={t0}
                  selected={selected === item}
                  onSelect={() => {
                    setSelected(item);
                    setPlaying(false);
                    seek(item.ts - t0);
                  }}
                />
              ))}
              {visibleRows.length > rowWindow.end ? (
                <div style={{ height: (visibleRows.length - rowWindow.end) * ROW_HEIGHT }} />
              ) : null}
              {!visibleRows.length ? (
                <div style={{ padding: 14, color: 'var(--text-dim)', fontSize: 12 }}>
                  Nothing in this stream for this session.
                </div>
              ) : null}
              {visibleRows.length > 0 ? (
                <div className="inspector-playhead" style={{ top: inspectorCursorTop }} aria-label={`Playback position ${fmt(elapsed)}`}>
                  <span className={playbackRowIndex < 0 ? 'before-first' : ''}>Now {fmt(elapsed)}</span>
                </div>
              ) : null}
            </div>
          </div>

          <Detail item={selected} follow={follow} onFollowToggle={() => setFollow((value) => !value)} />
        </aside>
      </div>

      {/* ── timeline ── */}
      <footer className="timeline">
        <div className="transport">
          <button className="t-btn" onClick={() => step(-1)} title="Previous event">
            ⏮
          </button>
          <button className="t-btn play" onClick={togglePlay} aria-label={playing ? 'Pause replay' : 'Play replay'} title={playing ? 'Pause (Space)' : 'Play (Space)'}>
            {playing ? '❚❚' : '▶'}
          </button>
          <button className="t-btn" onClick={() => step(1)} title="Next event">
            ⏭
          </button>
          <span className="timecode">
            {fmt(elapsed)} <span>/ {fmt(duration)}</span>
          </span>
          <div className="speed">
            {SPEEDS.map((s) => (
              <button key={s} className={speed === s ? 'on' : ''} onClick={() => changeSpeed(s)}>
                {s}×
              </button>
            ))}
          </div>
          <button className="btn-ghost jump-error" onClick={jumpToError}>Jump to next error</button>
        </div>

        <div className="lanes">
          <div className="lane-labels">
            <div>
              <span className="sw" style={{ background: 'var(--net)' }} />
              NETWORK
            </div>
            <div>
              <span className="sw" style={{ background: 'var(--msg)' }} />
              PAGE ⇄ CX
            </div>
            <div>
              <span className="sw" style={{ background: 'var(--emr)' }} />
              CONSOLE · EMR
            </div>
            <div>
              <span className="sw" style={{ background: 'var(--cx)' }} />
              CONSOLE · CX
            </div>
          </div>

          <div className="track-wrap" ref={trackRef} title="Drag to scrub · ← → step 5s · Space plays">
            <div className="segments">
              {segments.length ? (
                segments.map((seg, i) => (
                  <div
                    key={i}
                    className={`seg${elapsed >= seg.start && elapsed < seg.end ? ' cur' : ''}`}
                    style={{
                      left: `${((seg.start / duration) * 100).toFixed(3)}%`,
                      width: `${(((seg.end - seg.start) / duration) * 100).toFixed(3)}%`,
                    }}
                    title={`${seg.label}${seg.noteType ? ` · ${seg.noteType}` : ''}`}
                    onClick={() => seek(seg.start, playing)}
                  >
                    <span className="nav-ic">⇥</span>
                    {seg.label}
                  </div>
                ))
              ) : (
                <div className="seg" style={{ left: 0, right: 0 }}>
                  no page navigations captured
                </div>
              )}
            </div>

            <div className="ruler">
              {[0, 0.25, 0.5, 0.75, 1].map((f) => (
                <span key={f} style={{ left: `${f * 100}%`, transform: `translateX(-${f * 100}%)` }}>
                  {fmt(duration * f)}
                </span>
              ))}
            </div>

            <div className="tracks">
              {laneItems.map((items, lane) => (
                <div className="lane" key={lane}>
                  {items.map((item, i) => (
                    <span
                      key={i}
                      className={`ev ${
                        item.failed
                          ? 'err'
                          : lane === 0
                            ? 'net'
                            : lane === 1
                              ? 'msg'
                              : lane === 2
                                ? 'con-emr'
                                : 'con-cx'
                      }${item.count > 1 ? ' cluster' : ''}`}
                      style={{ left: `${pct(item.ts)}%` }}
                      title={`${fmt(item.ts - t0)} · ${item.label}${item.count > 1 ? ` (+${item.count - 1} more)` : ''}`}
                      onClick={() => {
                        setPlaying(false);
                        seek(item.ts - t0);
                        setSelected(item);
                      }}
                    >
                      {item.count > 1 ? `×${item.count}` : ''}
                    </span>
                  ))}
                </div>
              ))}
            </div>

            <div className="playhead" style={{ left: `${(elapsed / duration) * 100}%` }}>
              <span className="ph-time">{fmt(elapsed)}</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

/* ───────────────────────── rows & detail ───────────────────────── */

const InspectorRow = ({ item, t0, selected, onSelect }) => {
  const time = fmt(item.ts - t0);
  return (
    <div className={`inspector-row${selected ? ' sel' : ''}`} onClick={onSelect}>
      <span className="t">{time}</span>
      <div className="main-c">
        <div className="l1">
          {item.kind === 'net' ? (
            <>
              <span className="type-tag tnet">NET</span>
              <span className={`method ${item.method}`}>{(item.method || 'post').toUpperCase()}</span>
              {item.label}
            </>
          ) : null}
          {item.kind === 'msg' ? (
            <>
              <span className="type-tag tmsg">MSG</span>
              <span className={`dir ${item.dir === 'panel→page' ? 'c2p' : 'p2c'}`}>{item.dir || 'msg'}</span>
              <b>{item.label}</b>
            </>
          ) : null}
          {item.kind === 'con' ? (
            <>
              <span className={`type-tag ${item.source === 'cx' ? 'tcon-c' : 'tcon-e'}`}>
                {item.source === 'injector' ? 'INJECTOR' : item.source === 'emr' ? 'EMR' : 'CX'}
              </span>
              <span className={`lvl-${item.level === 'error' ? 'err' : item.level === 'warn' ? 'warn' : 'info'}`}>
                {item.label}
              </span>
            </>
          ) : null}
        </div>
      </div>
      <span className={`status${typeof item.status === 'number' ? (item.status >= 400 ? ' s4' : ' s2') : ''}`}>
        {item.kind === 'net' ? item.status : null}
        {/* A message row carries its own reply, so pairing is inherent rather
            than inferred — there is no correlation id to match on because the
            request and its response are logged as one event. */}
        {item.kind === 'msg' ? (
          item.row.error ? <span className="rt noreply">⇄ error</span> : <span className="rt">⇄ ok</span>
        ) : null}
      </span>
    </div>
  );
};

const Detail = ({ item, follow, onFollowToggle }) => {
  if (!item) {
    return (
      <div className="detail">
        <div className="detail-head">
          <b>NOTHING SELECTED</b>
          <span>click a row, a lane tick, or press play</span>
        </div>
        <div className="detail-actions">
          <button onClick={onFollowToggle}>Auto-select events: {follow ? 'on' : 'off'}</button>
        </div>
      </div>
    );
  }

  const { row, kind } = item;
  const blocks =
    kind === 'net'
      ? [
          {
            label: 'METADATA',
            value: {
              status: row.status,
              durationMs: row.durationMs,
              requestBytes: row.requestBytes,
              responseBytes: row.responseBytes,
            },
          },
          ...('request' in row
            ? [
                { label: 'REQUEST', value: row.request, tone: 'request' },
                { label: 'RESPONSE', value: row.response, tone: 'response' },
              ]
            : []),
          ...(row.error ? [{ label: 'ERROR', value: row.error, tone: 'error' }] : []),
        ]
      : kind === 'msg'
        ? [
            { label: 'REQUEST', value: row.args, tone: 'request' },
            { label: 'RESPONSE', value: row.response, tone: 'response' },
            ...(row.error ? [{ label: 'ERROR', value: row.error, tone: 'error' }] : []),
          ]
        : [
            { label: 'MESSAGE', value: row.message },
            ...(row.stack ? [{ label: 'STACK', value: row.stack }] : []),
          ];

  return (
    <div className="detail">
      <div className="detail-head">
        <b>{kind === 'con' ? `${(row.level || 'log').toUpperCase()} CONSOLE` : item.label}</b>
        <span>
          {kind === 'net'
            ? ('request' in row ? 'api · bodies captured' : 'api · metadata only')
            : kind === 'msg' ? row.channel : row.source}
        </span>
      </div>
      {blocks.map((block) => (
        <DetailBlock key={block.label} {...block} />
      ))}
      <div className="detail-actions">
        <button onClick={onFollowToggle}>Auto-select events: {follow ? 'on' : 'off'}</button>
      </div>
    </div>
  );
};

const DetailBlock = ({ label, value, tone = '' }) => {
  let text;
  try {
    text = typeof value === 'string' ? value : JSON.stringify(value, null, 2);
  } catch (e) {
    text = String(value);
  }

  return (
    <div className={`detail-block ${tone}`}>
      <div className="detail-block-label">{label}</div>
      <pre>{text === undefined ? '—' : text}</pre>
    </div>
  );
};

export default ReplayEngine;
